/*
 * Collection menu section.
 * - category chips with scroll spy, price / shelf-life / sort filters, search
 * - ADD button -> quantity stepper, driven by /cart.js
 * - multi-variant products open a bottom sheet containing a real <form is="product-form">,
 *   so adds go through the theme's AJAX flow and the pincode gate (assets/pincode.js)
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents, format) {
    format = format || (window.themeVariables && window.themeVariables.settings.moneyFormat) || '₹{{amount}}';
    function fmt(n, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var num = (n / 100).toFixed(precision);
      var parts = num.split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, name) {
      switch (name) {
        case 'amount': return fmt(cents, 2);
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  // lifted widgets are tracked as [element, property, previous value, previous priority]
  function restoreBottom(l) {
    if (l[2]) l[0].style.setProperty(l[1], l[2], l[3]); else l[0].style.removeProperty(l[1]);
  }

  function notify(el, status, message) {
    (el || document.documentElement).dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true, cancelable: true, detail: { status: status, error: message || '' }
    }));
  }

  function CollectionMenu(root) {
    this.root = root;
    this.bound = [];
    this.pending = {};
    this.lastVariant = {};
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-cm-item]'));
    this.cats = Array.prototype.slice.call(root.querySelectorAll('[data-cm-cat]'));
    this.chips = Array.prototype.slice.call(root.querySelectorAll('[data-cm-chip]'));
    this.chipRail = root.querySelector('[data-cm-chips]');
    this.rail = root.querySelector('[data-cm-bar]') || root.querySelector('.cm__rail');
    this.empty = root.querySelector('[data-cm-empty]');
    // active: filter-chip id -> its dataset; q: search text
    this.state = { active: {}, q: '' };
    this.filterChips = Array.prototype.slice.call(root.querySelectorAll('[data-cm-filter]'));
    this.cart = { items: [], item_count: 0, total_price: 0 };
    this.sheetOpen = false;
    this.bindFilters();
    this.bindChips();
    this.bindControls();
    this.bindSheet();
    this.bindCartEvents();
    this.bindAdd();
    this.watchFloaters();
    this.watchRail();
    this.apply();
    this.refreshCart();
  }

  // document / window listeners are tracked so a re-rendered section can release them
  CollectionMenu.prototype.listen = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.bound.push([target, type, fn, opts]);
  };

  CollectionMenu.prototype.destroy = function () {
    this.bound.forEach(function (b) { b[0].removeEventListener(b[1], b[2], b[3]); });
    this.bound = [];
    if (this.io) this.io.disconnect();
    if (this.mo) this.mo.disconnect();
    if (this.ro) this.ro.disconnect();
    this.showFloaters();
    (this.lifted || []).forEach(restoreBottom);
    this.lifted = [];
    document.body.style.overflow = '';
  };

  /* ---------- filters + search ---------- */
  CollectionMenu.prototype.syncFilterChips = function () {
    var self = this;
    this.filterChips.forEach(function (b) {
      var on = !!self.state.active[b.dataset.cmFilter];
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on);
    });
  };

  CollectionMenu.prototype.bindFilters = function () {
    var self = this;
    this.filterChips.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.dataset.cmFilter;
        if (self.state.active[id]) {
          delete self.state.active[id];
        } else {
          self.state.active[id] = btn.dataset;
          // the two sort orders are mutually exclusive
          if (btn.dataset.kind === 'sort_asc' || btn.dataset.kind === 'sort_desc') {
            self.filterChips.forEach(function (o) {
              if (o !== btn && (o.dataset.kind === 'sort_asc' || o.dataset.kind === 'sort_desc')) delete self.state.active[o.dataset.cmFilter];
            });
          }
        }
        self.syncFilterChips();
        self.apply();
      });
    });
    var input = this.root.querySelector('[data-cm-search]');
    var xBtn = this.root.querySelector('[data-cm-search-clear]');
    var clear = this.root.querySelector('[data-cm-clear]');
    function run() {
      self.state.q = input.value.trim().toLowerCase();
      if (xBtn) xBtn.hidden = !input.value;
      self.apply();
      if (self.state.q) self.showResults();
    }
    if (clear) clear.addEventListener('click', function () {
      self.state = { active: {}, q: '' };
      if (input) input.value = '';
      if (xBtn) xBtn.hidden = true;
      self.syncFilterChips();
      self.apply();
      self.showResults();
    });
    if (xBtn) xBtn.addEventListener('click', function () {
      input.value = '';
      xBtn.hidden = true;
      self.state.q = '';
      self.apply();
      self.showResults();
      input.focus();
    });
    var t;
    if (input) {
      input.addEventListener('input', function () {
        if (xBtn) xBtn.hidden = !input.value;
        clearTimeout(t);
        t = setTimeout(run, 140);
      });
    }
  };

  // Brings the top of the results just under the sticky rail, only when the shopper has scrolled past it.
  // Instant (not smooth) so it never fights with typing; the search lives in the rail, so it stays visible.
  CollectionMenu.prototype.showResults = function () {
    var main = this.root.querySelector('[data-cm-main]');
    if (!main || !this.rail) return;
    var railBottom = this.rail.getBoundingClientRect().bottom;
    var top = main.getBoundingClientRect().top;
    if (top < railBottom) window.scrollBy({ top: top - railBottom - 8, behavior: 'auto' });
  };

  CollectionMenu.prototype.watchRail = function () {
    var self = this;
    if (!this.rail || !('ResizeObserver' in window)) return;
    function set() { self.root.style.setProperty('--cm-rail-h', self.rail.offsetHeight + 'px'); }
    set();
    this.ro = new ResizeObserver(set);
    this.ro.observe(this.rail);
  };

  // Within one filter type the active chips are OR-ed (two price bands = either band); across types they are AND-ed.
  CollectionMenu.prototype.matches = function (item) {
    var s = this.state;
    var price = parseInt(item.dataset.minPrice, 10) || 0;
    var groups = {};
    Object.keys(s.active).forEach(function (id) {
      var f = s.active[id];
      (groups[f.kind] = groups[f.kind] || []).push(f);
    });
    var tags = (item.dataset.tags || '').split('|');
    var ok = Object.keys(groups).every(function (kind) {
      if (kind === 'sort_asc' || kind === 'sort_desc') return true;
      if (kind === 'available') return item.dataset.available === 'true';
      return groups[kind].some(function (f) {
        switch (kind) {
          case 'price':
            return (f.min === '' || price >= parseInt(f.min, 10)) && (f.max === '' || price <= parseInt(f.max, 10));
          case 'shelf':
            return (parseInt(item.dataset.shelf, 10) || 0) >= (parseInt(f.days, 10) || 0);
          case 'tag':
            return !!f.value && tags.indexOf(f.value) !== -1;
          case 'type':
            return !!f.value && item.dataset.type === f.value;
        }
        return true;
      });
    });
    if (!ok) return false;
    if (s.q) {
      var hay = item.dataset.search || '';
      if (!s.q.split(/\s+/).every(function (w) { return hay.indexOf(w) !== -1; })) return false;
    }
    return true;
  };

  CollectionMenu.prototype.sortDir = function () {
    var dir = 0;
    var a = this.state.active;
    Object.keys(a).forEach(function (id) {
      if (a[id].kind === 'sort_asc') dir = 1;
      if (a[id].kind === 'sort_desc') dir = -1;
    });
    return dir;
  };

  CollectionMenu.prototype.apply = function () {
    var self = this, any = false, dir = this.sortDir();
    this.cats.forEach(function (cat) {
      var list = cat.querySelector('[data-cm-list]');
      var items = Array.prototype.slice.call(list.querySelectorAll('[data-cm-item]'));
      items.sort(function (a, b) {
        var byIndex = a.dataset.index - b.dataset.index;
        return dir ? (dir * (parseInt(a.dataset.minPrice, 10) - parseInt(b.dataset.minPrice, 10))) || byIndex : byIndex;
      });
      var visible = 0;
      items.forEach(function (item) {
        var ok = self.matches(item);
        item.hidden = !ok;
        if (ok) visible++;
        list.appendChild(item);
      });
      cat.hidden = visible === 0;
      var count = cat.querySelector('[data-cm-count]');
      if (count) count.textContent = visible + (visible === 1 ? ' item' : ' items');
      if (visible) any = true;
    });
    this.chips.forEach(function (chip) {
      var cat = self.root.querySelector('[data-cm-cat="' + chip.dataset.cmChip + '"]');
      chip.hidden = !cat || cat.hidden;
    });
    if (this.empty) this.empty.hidden = any;
    this.observe();
  };

  /* ---------- chips + scroll spy ---------- */
  CollectionMenu.prototype.scrollTo = function (el) {
    if (!el) return;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };

  CollectionMenu.prototype.setActive = function (id) {
    if (id === this.activeChip) return;
    this.activeChip = id;
    var rail = this.chipRail;
    this.chips.forEach(function (chip) {
      var on = chip.dataset.cmChip === id;
      chip.classList.toggle('is-active', on);
      if (on) chip.setAttribute('aria-current', 'true'); else chip.removeAttribute('aria-current');
      if (on) rail.scrollTo({ left: chip.offsetLeft - rail.clientWidth / 2 + chip.clientWidth / 2, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  };

  CollectionMenu.prototype.bindChips = function () {
    var self = this;
    this.chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var cat = self.root.querySelector('[data-cm-cat="' + chip.dataset.cmChip + '"]');
        if (!cat || cat.hidden) return;
        self.setActive(chip.dataset.cmChip);
        self.scrollTo(cat);
      });
    });
  };

  CollectionMenu.prototype.observe = function () {
    var self = this;
    if (this.io) this.io.disconnect();
    if (!('IntersectionObserver' in window)) return;
    var top = this.rail ? Math.round(this.rail.getBoundingClientRect().bottom) + 10 : 150;
    this.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) self.setActive(en.target.dataset.cmCat); });
    }, { rootMargin: '-' + top + 'px 0px -60% 0px' });
    this.cats.forEach(function (c) { if (!c.hidden) self.io.observe(c); });
  };

  /* ---------- ADD / stepper controls ---------- */
  CollectionMenu.prototype.bindControls = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var item = e.target.closest('[data-cm-item]');
      if (!item) return;
      if (e.target.closest('[data-cm-open]')) { self.openSheet(item, e.target.closest('[data-cm-open]')); return; }
      if (e.target.closest('[data-cm-inc]')) { self.step(item, +1); return; }
      if (e.target.closest('[data-cm-dec]')) { self.step(item, -1); }
    });
    // fly-to-cart for single-variant adds (the event is dispatched on the form by ProductForm)
    this.root.addEventListener('variant:added', function (e) {
      var item = e.target.closest && e.target.closest('[data-cm-item]');
      if (item) self.fly(item.querySelector('.cm__thumb img'));
    }, true);
  };

  CollectionMenu.prototype.linesFor = function (productId) {
    return this.cart.items.filter(function (l) { return String(l.product_id) === String(productId) && l.quantity > 0; });
  };

  // Optimistic: the count and the cart bar change on tap, and one cart/change.js call is sent once taps pause.
  CollectionMenu.prototype.step = function (item, delta) {
    var self = this;
    var lines = this.linesFor(item.dataset.productId);
    if (!lines.length) return;
    if (delta > 0 && item.dataset.multi === 'true') { this.openSheet(item, item.querySelector('[data-cm-inc]')); return; }
    // single variant: its only line. Multi variant: the most recently added line.
    var line = lines[lines.length - 1];
    var qty = Math.max(0, line.quantity + delta);
    this.cart.item_count += qty - line.quantity;
    this.cart.total_price += (qty - line.quantity) * line.final_price;
    line.quantity = qty;
    this.render();

    var p = this.pending[line.key] || (this.pending[line.key] = {});
    p.qty = qty;
    p.el = item;
    clearTimeout(p.timer);
    p.timer = setTimeout(function () { self.flush(line.key); }, 350);
  };

  CollectionMenu.prototype.flush = function (key) {
    var self = this, p = this.pending[key];
    if (!p) return;
    delete this.pending[key];
    post(window.themeVariables.routes.cartChangeUrl + '.js', { id: key, quantity: p.qty, sections: 'mini-cart' }).then(function (cart) {
      if (!cart || !cart.items) {
        notify(p.el, 'error', cart && (cart.description || cart.message));
        return self.refreshCart(null, true);
      }
      // newer taps are still waiting: don't overwrite their optimistic count
      if (Object.keys(self.pending).length) return;
      self.cart = cart;
      self.render();
      self.broadcast(cart);
    }).catch(function () {
      notify(p.el, 'error', 'Something went wrong. Please try again.');
      self.refreshCart(null, true);
    });
  };

  // Updates the header count and drawer contents without opening the drawer.
  CollectionMenu.prototype.broadcast = function (cart) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: false } }));
  };

  /* ---------- add to cart (our own handler, so the cart drawer never opens by itself) ---------- */
  CollectionMenu.prototype.bindAdd = function () {
    var self = this;
    this.root.addEventListener('submit', function (e) {
      var form = e.target.closest && e.target.closest('form[data-cm-add]');
      if (!form) return;
      e.preventDefault();
      self.add(form);
    });
  };

  CollectionMenu.prototype.add = function (form) {
    var self = this;
    if (form.hasAttribute('data-cm-busy')) return;
    var buttons = Array.prototype.filter.call(form.elements, function (el) { return el.type === 'submit'; });
    function busy(on) {
      if (on) form.setAttribute('data-cm-busy', ''); else form.removeAttribute('data-cm-busy');
      buttons.forEach(function (b) {
        if (on) { b.setAttribute('aria-busy', 'true'); b.setAttribute('disabled', 'disabled'); }
        else { b.removeAttribute('aria-busy'); b.removeAttribute('disabled'); }
      });
    }
    var body = new FormData(form);
    body.append('sections', 'mini-cart');
    busy(true);
    fetch(window.themeVariables.routes.cartAddUrl + '.js', { method: 'POST', body: body, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
      .then(function (res) {
        busy(false);
        if (!res.ok) { notify(form, 'error', res.json && (res.json.description || res.json.message)); return; }
        if (window.themeVariables.settings.cartType === 'page') { window.location.href = window.themeVariables.routes.cartUrl; return; }
        var added = res.json.items ? res.json.items[0] : res.json;
        if (added) self.lastVariant[String(added.product_id)] = added.variant_id || added.id;
        form.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: added } }));
        return fetch(window.themeVariables.routes.cartUrl + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            self.cart = cart;
            self.render();
            cart.sections = res.json.sections;
            self.broadcast(cart);
          });
      })
      .catch(function () {
        busy(false);
        notify(form, 'error', 'Something went wrong. Please try again.');
      });
  };

  CollectionMenu.prototype.render = function () {
    var self = this;
    this.items.forEach(function (item) {
      var lines = self.linesFor(item.dataset.productId);
      var qty = lines.reduce(function (a, l) { return a + l.quantity; }, 0);
      var ctrl = item.querySelector('[data-cm-ctrl]');
      var stepper = item.querySelector('[data-cm-stepper]');
      var n = item.querySelector('[data-cm-qty]');
      if (!ctrl || !stepper) return;
      var changed = n.textContent !== String(qty);
      ctrl.classList.toggle('is-in-cart', qty > 0);
      stepper.hidden = qty === 0;
      n.textContent = qty;
      if (changed && qty > 0) { n.classList.remove('is-tick'); void n.offsetWidth; n.classList.add('is-tick'); }
    });
    var bar = this.root.querySelector('[data-cm-cartbar]');
    if (bar) {
      var count = this.cart.item_count || 0;
      bar.querySelector('[data-cm-bar-count]').textContent = count + (count === 1 ? ' item added' : ' items added');
      bar.querySelector('[data-cm-bar-total]').textContent = formatMoney(this.cart.total_price || 0);
      bar.classList.toggle('is-on', count > 0 && !this.sheetOpen);
    }
    this.liftFloaters();
  };

  CollectionMenu.prototype.refreshCart = function (cart, force) {
    var self = this;
    if (!force && cart && cart.items) {
      if (Object.keys(this.pending).length) return; // keep optimistic counts until the change lands
      this.cart = cart; this.render(); return;
    }
    fetch((window.themeVariables && window.themeVariables.routes.cartUrl || '/cart') + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { self.cart = c; self.render(); })
      .catch(function () {});
  };

  CollectionMenu.prototype.bindCartEvents = function () {
    var self = this;
    function onCart(e) {
      var cart = e.detail && e.detail.cart;
      if (self.sheetOpen && e.type === 'cart:refresh') self.closeSheet(true);
      self.refreshCart(cart);
    }
    this.listen(document.documentElement, 'cart:updated', onCart);
    this.listen(document.documentElement, 'cart:refresh', onCart);
    this.listen(window, 'pageshow', function (e) { if (e.persisted) self.refreshCart(null, true); });
    var bar = this.root.querySelector('[data-cm-cartbar-open]');
    if (bar) bar.addEventListener('click', function () {
      var drawer = document.getElementById('mini-cart');
      if (drawer) drawer.open = true; else window.location.href = window.themeVariables.routes.cartUrl;
    });
  };

  CollectionMenu.prototype.fly = function (img) {
    if (!img || reduceMotion || !img.animate) return;
    var target = this.root.querySelector('[data-cm-cartbar].is-on button') || document.querySelector('[aria-controls="mini-cart"]');
    if (!target) return;
    var r = img.getBoundingClientRect(), t = target.getBoundingClientRect();
    var f = img.cloneNode();
    f.removeAttribute('srcset');
    f.style.cssText = 'position:fixed;z-index:60;pointer-events:none;border-radius:14px;object-fit:cover;box-shadow:0 10px 30px rgba(0,0,0,.3);left:' + r.left + 'px;top:' + r.top + 'px;width:' + r.width + 'px;height:' + r.height + 'px';
    document.body.appendChild(f);
    var dx = t.left + t.width / 2 - (r.left + r.width / 2), dy = t.top + t.height / 2 - (r.top + r.height / 2);
    f.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: 'translate(' + dx * 0.5 + 'px,' + (dy * 0.5 - 80) + 'px) scale(.55)', opacity: 1, offset: 0.5 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.12)', opacity: 0.3 }
    ], { duration: 650, easing: 'cubic-bezier(.5,0,.3,1)' }).onfinish = function () { f.remove(); };
  };

  /* ---------- bottom sheet ---------- */
  CollectionMenu.prototype.bindSheet = function () {
    var self = this;
    this.sheet = this.root.querySelector('[data-cm-sheet]');
    this.scrim = this.root.querySelector('[data-cm-scrim]');
    this.slot = this.root.querySelector('[data-cm-sheet-slot]');
    this.scrim.addEventListener('click', function () { self.closeSheet(); });
    this.root.querySelector('[data-cm-close]').addEventListener('click', function () { self.closeSheet(); });
    this.listen(document, 'keydown', function (e) {
      if (!self.sheetOpen) return;
      if (e.key === 'Escape') { self.closeSheet(); return; }
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(self.sheet.querySelectorAll('button, input, a[href], [tabindex]:not([tabindex="-1"])'), function (el) {
        return !el.disabled && el.offsetParent !== null;
      });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || !self.sheet.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // ProductForm dispatches variant:added on the form once the add succeeded
    this.sheet.addEventListener('variant:added', function () { self.closeSheet(true); }, true);

    // drag down to dismiss
    var y0 = null, dy = 0, sheet = this.sheet;
    sheet.addEventListener('touchstart', function (e) {
      var body = sheet.querySelector('.cm__sheet-body');
      if (body && body.scrollTop > 0 && !e.target.closest('.cm__grab')) return;
      y0 = e.touches[0].clientY; dy = 0;
    }, { passive: true });
    sheet.addEventListener('touchmove', function (e) {
      if (y0 === null) return;
      dy = e.touches[0].clientY - y0;
      if (dy > 0) { sheet.classList.add('is-drag'); sheet.style.transform = 'translateY(' + dy + 'px)'; }
    }, { passive: true });
    sheet.addEventListener('touchend', function () {
      if (y0 === null) return;
      sheet.classList.remove('is-drag');
      if (dy > 110) self.closeSheet(); else sheet.style.transform = '';
      y0 = null;
    });
  };

  // Third-party floating widgets (loyalty, WhatsApp) use the maximum z-index and would cover the drawer's buttons.
  // Hide them while the drawer is open and restore them afterwards.
  CollectionMenu.prototype.hideFloaters = function () {
    var hidden = [];
    var host = document.getElementById('xeno-loyalty-runtime-host');
    var els = host ? [host] : [];
    document.querySelectorAll('body *').forEach(function (el) {
      if (el.closest('[data-cm]')) return;
      var cs = getComputedStyle(el);
      if (cs.position === 'fixed' && parseInt(cs.zIndex, 10) > 100000) els.push(el);
    });
    els.forEach(function (el) { hidden.push([el, el.style.display]); el.style.display = 'none'; });
    this.floaters = hidden;
  };
  CollectionMenu.prototype.showFloaters = function () {
    (this.floaters || []).forEach(function (f) { f[0].style.display = f[1]; });
    this.floaters = [];
  };

  CollectionMenu.prototype.watchFloaters = function () {
    var self = this, t;
    function later() { clearTimeout(t); t = setTimeout(function () { self.liftFloaters(); }, 300); }
    this.mo = new MutationObserver(later);
    this.mo.observe(document.body, { childList: true });
    this.listen(window, 'load', later);
    this.listen(window, 'resize', later);
  };

  // Floating widgets (WhatsApp, chat) sit at the bottom corner with the maximum z-index, on top of the cart bar.
  // While the bar is showing, nudge any widget that would overlap it up above the bar; put it back afterwards.
  CollectionMenu.prototype.liftFloaters = function () {
    var self = this;
    var bar = this.root.querySelector('[data-cm-cartbar]');
    this.lifted = this.lifted || [];
    // phones only: on larger screens the cart bar doesn't reach the corner widgets
    var on = bar && bar.classList.contains('is-on') && window.matchMedia('(max-width: 760px)').matches;
    if (!on) {
      this.lifted.forEach(restoreBottom);
      this.lifted = [];
      return;
    }
    var cs = getComputedStyle(bar);
    var clearance = (parseFloat(cs.bottom) || 0) + bar.offsetHeight + 12;
    var br = bar.getBoundingClientRect();
    document.querySelectorAll('body *').forEach(function (el) {
      if (el.closest('[data-cm]') || self.lifted.some(function (l) { return l[0] === el; })) return;
      var c = getComputedStyle(el);
      if (c.position !== 'fixed' || !(parseInt(c.zIndex, 10) > 100000)) return;
      var r = el.getBoundingClientRect();
      if (!r.width || r.right < br.left || r.left > br.right) return;
      var gap = window.innerHeight - r.bottom;
      if (gap >= clearance - 12) return; // already clear of the bar
      self.lifted.push([el, 'bottom', el.style.getPropertyValue('bottom'), el.style.getPropertyPriority('bottom')]);
      el.style.setProperty('bottom', clearance + 'px', 'important');
    });
    // the rewards widget keeps its button inside a shadow root, so it is moved with `translate`
    var host = document.getElementById('xeno-loyalty-runtime-host');
    if (host && host.shadowRoot) {
      var barTop = window.innerHeight - (parseFloat(cs.bottom) || 0) - bar.offsetHeight;
      host.shadowRoot.querySelectorAll('button').forEach(function (el) {
        if (self.lifted.some(function (l) { return l[0] === el; })) return;
        if (getComputedStyle(el).position !== 'fixed') return;
        var r = el.getBoundingClientRect();
        if (!r.width || r.width > 120 || r.bottom <= barTop || r.right < br.left || r.left > br.right) return;
        self.lifted.push([el, 'translate', el.style.getPropertyValue('translate'), el.style.getPropertyPriority('translate')]);
        el.style.setProperty('translate', '0 -' + Math.ceil(r.bottom - barTop + 12) + 'px', 'important');
      });
    }
  };

  CollectionMenu.prototype.openSheet = function (item, opener) {
    var tpl = item.querySelector('[data-cm-sheet-template]');
    if (!tpl) return;
    var self = this;
    this.lastFocus = opener || document.activeElement;
    this.slot.replaceChildren(document.importNode(tpl.content, true));
    var form = this.slot.querySelector('form');
    var qty = 1;
    var qtyInput = form.querySelector('[data-cm-quantity]');
    var qtyLabel = form.querySelector('[data-cm-sheet-qty]');
    var total = form.querySelector('[data-cm-sheet-total]');
    function update() {
      var checked = form.querySelector('input[name="id"]:checked');
      var price = checked ? parseInt(checked.dataset.price, 10) : 0;
      qtyInput.value = qty;
      qtyLabel.textContent = qty;
      qtyLabel.classList.remove('is-tick'); void qtyLabel.offsetWidth; qtyLabel.classList.add('is-tick');
      total.textContent = formatMoney(price * qty);
    }
    form.addEventListener('change', update);
    form.querySelector('[data-cm-sheet-dec]').addEventListener('click', function () { qty = Math.max(1, qty - 1); update(); });
    form.querySelector('[data-cm-sheet-inc]').addEventListener('click', function () { qty = Math.min(99, qty + 1); update(); });
    var lines = this.linesFor(item.dataset.productId);
    var preferred = this.lastVariant[String(item.dataset.productId)] || (lines.length && lines[lines.length - 1].variant_id);
    var radio = preferred && form.querySelector('input[name="id"][value="' + preferred + '"]:not(:disabled)');
    if (radio) radio.checked = true;
    lines.forEach(function (l) {
      var tag = form.querySelector('[data-cm-incart="' + l.variant_id + '"]');
      if (tag) { tag.textContent = 'In cart: ' + l.quantity; tag.hidden = false; }
    });
    update();

    this.sheetOpen = true;
    this.sheet.hidden = false;
    void this.sheet.offsetWidth;
    this.sheet.classList.add('is-on');
    this.scrim.classList.add('is-on');
    document.body.style.overflow = 'hidden';
    this.hideFloaters();
    this.render();
    setTimeout(function () { var c = self.root.querySelector('[data-cm-close]'); if (c) c.focus({ preventScroll: true }); }, 350);
  };

  CollectionMenu.prototype.closeSheet = function (skipFocus) {
    var self = this, sheet = this.sheet;
    if (!this.sheetOpen) return;
    this.sheetOpen = false;
    sheet.classList.remove('is-on');
    sheet.style.transform = '';
    this.scrim.classList.remove('is-on');
    document.body.style.overflow = '';
    this.showFloaters();
    setTimeout(function () { if (!self.sheetOpen) { sheet.hidden = true; self.slot.replaceChildren(); } }, 500);
    this.render();
    if (skipFocus !== true && this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-cm]').forEach(function (el) {
      if (!el._cm) el._cm = new CollectionMenu(el);
    });
  }

  init();
  // theme editor: re-init when a section is (re)loaded
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-cm]').forEach(function (el) { if (el._cm) { el._cm.destroy(); el._cm = null; } });
  });
})();
